import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { AuditoriasLista } from "../../domain/models/auditorias_lista";
import { useAuditoriasFilters } from "../hooks/Auditoria_auditorias/filter";
import { useAuditoriasPagination } from "../hooks/Auditoria_auditorias/pagination";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useProcessoAuditoriasPagination } from "../hooks/Auditoria_auditorias/processo_auditoria_pagination";
import { AuditoriaCopiaForm } from "../schemas/Auditoria_auditoria/auditoria_copia";
import { defaultReportPlanoAuditoriaValues, ReportPlanoAuditoriaForm, ReportPlanoAuditoriaSchema } from "../schemas/Auditoria_auditoria/report_plano_treinamento";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useNavigate, useParams } from "react-router";
import dayjs from "dayjs";

type LoadAuditoriasListaFuncState = {
  IdAuditoria?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  status?: number,
  pesquisa?: string | number | Date,
};


export const useAuditoriasAuditoriasViewModel = () => {
  const {
    commons: { },
    reports: {getReportPlanoAuditoriaUseCase},
    auditor: {getAllListagemAuditoriaUseCase , deleteAuditoriaUseCase, copiaAuditoriaUseCase }
  } = useDependencyInjection();
  const { s } = useParams();

  //#region Relatorio
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  //#endregion
  const navigate = useNavigate();
 
  const hookFormReport = useForm<ReportPlanoAuditoriaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultReportPlanoAuditoriaValues,
    resolver: zodResolver(ReportPlanoAuditoriaSchema),
  });


  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [auditoriasLista, setAuditoriasLista] = useState<AuditoriasLista[]>([]);
  const [selectedAuditoria, setSelectedAuditoria] = useState<AuditoriasLista | undefined> (undefined);
  const [openCopiaModal, setOpenCopiaModal] = useState<boolean>(false);

  //Arquivo
  const [AuditoriasDeleteModal, setAuditoriasDeleteModal] = useState<boolean>(false);


  const {pushErrorNotification, setCustomLoading, pushInfoNotification } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useAuditoriasFilters();
  const pagination = useAuditoriasPagination();
  const paginationProcess = useProcessoAuditoriasPagination();
  

  // #region Funções de carregamento

  const loadAuditoriasListaState = useCallback(async (funcState?: LoadAuditoriasListaFuncState) => {
      try {
        if(s !== undefined && s !== null) filters.status = Number(s);

        const filterID_Auditoria = evaluateParamValue(funcState?.IdAuditoria, filters.IdAuditoria);
        const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
        const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
        const filteredStatus = funcState?.status ?? filters.status;
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        await getAllListagemAuditoriaUseCase.getAllListagemAuditoria({
              IdAuditoria: filterID_Auditoria, PesquisarPor: filteredTipoPesquisa, Pesquisar: String(filteredPesquisa ), status: filteredStatus
            },
            filteredPage,
            filteredRowsPerPage
        );
        setAuditoriasLista(getAllListagemAuditoriaUseCase.items);
        pagination.setRowsPerPage(getAllListagemAuditoriaUseCase.pageSize);
        pagination.setTotalItems(getAllListagemAuditoriaUseCase.totalItems);
      } catch (e: unknown) {
          throw new Error("Falha ao buscar Auditorias!");
      }
    }, [filters, getAllListagemAuditoriaUseCase, pagination, s]);


  //#endregion

  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      jobs.push(loadAuditoriasListaState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadAuditoriasListaState());
      
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadAuditoriasListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadAuditoriasListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadAuditoriasListaState, pagination, pushErrorNotification]);

  
  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
  
        await loadAuditoriasListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadAuditoriasListaState, pagination, pushErrorNotification]);


  const handleCadastroOpenState = useCallback((id: number | null) => {
    if(id) navigate(`/Auditorias_cadastro/${id}`);
    else navigate(`/Auditorias_cadastro/`);
  }, [navigate]);

 
  const handleDeleteAuditoriaModalOpenState = useCallback((IdAuditoria: number | null, openState: boolean) => {
    if (openState) {
      if (IdAuditoria && auditoriasLista) {
        setSelectedAuditoria(auditoriasLista.find(x => x.IdAuditoria === IdAuditoria));
      } else {
        setSelectedAuditoria(undefined);
      }
    }
    setAuditoriasDeleteModal(openState);
  }, [auditoriasLista]);

  const handleDeleteConfirmaAuditoria = useCallback(async () => {
      if (selectedAuditoria) {
        try {
          setSaving(true);
          await deleteAuditoriaUseCase.deleteAuditoria(selectedAuditoria.IdAuditoria);
          await loadAuditoriasListaState();
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
        } finally {
          setSaving(false);
          setAuditoriasDeleteModal(false);
        }
      }
    }, [selectedAuditoria, deleteAuditoriaUseCase, loadAuditoriasListaState, pushErrorNotification]);


  //#endregion


  //#region Filtros
  const handleClearFilters = useCallback(async () => {
      try {
          setLoading(true);
          filters.clearFilters();
          pagination.setPage(1);
          await loadAuditoriasListaState();
      } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao buscar contas a receber!'));
      } finally {
          setLoading(false);
      }
  }, [filters, pagination, loadAuditoriasListaState, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback( debouncedRef,  (newValue: string | Date | number | null) => {
      useAuditoriasFilters.getState().setPesquisa(newValue);
      
      if (newValue) {
        return async () => {
          try {
            setLoading(true);
            useAuditoriasPagination.getState().setPage(1);
            await loadAuditoriasListaState({ pesquisa: newValue, page: 1 });
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar auditoria!"));
          } finally {
            setLoading(false);
          }
        }
      } else {
        return async () => {
          try {
            setLoading(true);
            useAuditoriasPagination.getState().setPage(1);
            await loadAuditoriasListaState(); 
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar auditoria!"));
          } finally {
            setLoading(false);
          }
        }
      }
    }, 
    [ loadAuditoriasListaState, pushErrorNotification, setLoading]
  );
  
  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useAuditoriasFilters.getState().setTipoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useAuditoriasPagination.getState().setPage(1);
        await loadAuditoriasListaState({ tipoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar listagem!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadAuditoriasListaState, pushErrorNotification]);

  const handleStatusChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
      useAuditoriasFilters.getState().setStatus(newValue);
      return async () => {
        try {
          setLoading(true);
          useAuditoriasPagination.getState().setPage(1);
          await loadAuditoriasListaState({ status: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar listagem!"));
        } finally {
          setLoading(false);
        }
      }
    }, [ loadAuditoriasListaState, pushErrorNotification]);

  //#endregion

  //#region Copia

  const handleAuditoriaCopiaOpenState = useCallback((IdAuditoria: number | null, openState: boolean) => {
      if (openState) {
          setSelectedAuditoria(auditoriasLista.find(x => x.IdAuditoria === IdAuditoria));
          setOpenCopiaModal(true);
      } else {
          setOpenCopiaModal(false);
      }
  }, [auditoriasLista]);
    
  const handleSubmitCopiaAuditoria = useCallback(async (form: AuditoriaCopiaForm) => {
      try {
          if (selectedAuditoria) {
            const payload = {
                ...form,
                dataAuditoria: dayjs(form.dataAuditoria).startOf('day').toDate()
            };
              setSaving(true);
              await copiaAuditoriaUseCase.copiaAuditoria(payload);
              await loadAuditoriasListaState();
              pushInfoNotification("Sucesso", "Auditoria copiada com sucesso!", { isAutoFinish: true });
          } 
      } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao copiar Auditoria!"));
      } finally {
          setOpenCopiaModal(false);
          setSaving(false);
      }
  }, [selectedAuditoria, copiaAuditoriaUseCase, loadAuditoriasListaState, pushInfoNotification, pushErrorNotification]);

  //#endregion
  
  //#region Relatoria
  const handleAuditoriaExportar = useCallback(async (IdAuditoria: number) => {
      try {
        setLoading(true);
        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportPlanoAuditoriaUseCase.getReportPlanoAuditoria(IdAuditoria, requiredMimeType),
        });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de auditorias!'));
      } finally {
        setLoading(false);
        setCustomLoading(false);
      }
    }, [getReportPlanoAuditoriaUseCase, pushErrorNotification, setCustomLoading, startReportViewerProcess]);

  //#endregion
  return {
      loading,
      saving,
      errorMessage,
      AuditoriasDeleteModal,
      auditoriasLista,
      pagination,
      filters,
      selectedAuditoria,
      paginationProcess,
      openCopiaModal,
      hookFormReport,
      reportViewerState,
      handleReload,
      handleRowsPerPageChange,
      handlePageChange,
      handleCadastroOpenState,
      handleDeleteConfirmaAuditoria,
      handleDeleteAuditoriaModalOpenState,
      handleTipoPesquisaChange,
      handlePesquisaChange,
      handleClearFilters,
      handleStatusChange,
      handleSubmitCopiaAuditoria,
      handleAuditoriaCopiaOpenState,
      handleAuditoriaExportar,
      
  }
}
